import { formatDisplayMoney, notifyVatRefresh } from '../utils/vatDisplay';
import { initSwiper } from '../components/swiper';
import { FOR_PRODUCT_PROPERTY, getLineItemProperty } from './warrantySelection';
import trapFocus from '../utils/trapFocus';
import { SETTINGS } from '../utils/constants';

const VISIBLE_CLASS = 'is-visible';
const CLOSE_SELECTOR = '[data-atc-notification-close]';

const WARRANTY_CHECK_ICON =
  '<svg class="warranty-selection-banner__icon-svg block h-4 w-[17px]" xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none" aria-hidden="true"><path d="M8.375 16C5.5 16 2.875 14.5 1.4375 12C0 9.53125 0 6.5 1.4375 4C2.875 1.53125 5.5 0 8.375 0C11.2188 0 13.8438 1.53125 15.2812 4C16.7188 6.5 16.7188 9.53125 15.2812 12C13.8438 14.5 11.2188 16 8.375 16ZM11.9062 6.53125H11.875C12.1875 6.25 12.1875 5.78125 11.875 5.46875C11.5938 5.1875 11.125 5.1875 10.8438 5.46875L7.375 8.96875L5.90625 7.5C5.59375 7.1875 5.125 7.1875 4.84375 7.5C4.53125 7.78125 4.53125 8.25 4.84375 8.53125L6.84375 10.5312C7.125 10.8438 7.59375 10.8438 7.90625 10.5312L11.9062 6.53125Z" fill="#00893E"/></svg>';

const getLineItems = cartAddResponse => {
  if (!cartAddResponse) return [];
  if (Array.isArray(cartAddResponse.items)) return cartAddResponse.items;
  if (cartAddResponse.id) return [cartAddResponse];
  return [];
};

const escapeHtml = value => {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
};

const getWarrantyStrings = () => window.theme?.strings?.warranty || {};

const formatWarrantySelectedLabel = (title, template) => {
  const labelTemplate = template || '{{ title }} selected';
  return labelTemplate.replace('{{ title }}', title);
};

const partitionAddedItems = items => {
  const productItems = [];
  const warrantyByParentId = new Map();

  items.forEach(item => {
    const parentProductId = getLineItemProperty(item, FOR_PRODUCT_PROPERTY);
    if (parentProductId) {
      warrantyByParentId.set(String(parentProductId), item);
      return;
    }
    productItems.push(item);
  });

  return { productItems, warrantyByParentId };
};

const renderWarrantyNestedHtml = warrantyItem => {
  const strings = getWarrantyStrings();
  const cents = warrantyItem.price;
  const label = escapeHtml(
    formatWarrantySelectedLabel(warrantyItem.variant_title || warrantyItem.title || '', strings.selected)
  );

  return `<div class="atc-notification-warranty w-full">
      <div class="warranty-selection-banner w-full self-stretch">
        <div class="warranty-selection-banner__content">
          <span class="warranty-selection-banner__icon" aria-hidden="true">${WARRANTY_CHECK_ICON}</span>
          <span class="warranty-selection-banner__label text-base text-black">${label}</span>
          <span class="warranty-selection-banner__price text-base font-heavy text-black">
            ${formatDisplayMoney(cents)}
          </span>
        </div>
      </div>
    </div>`;
};

const renderProductRowHtml = item => {
  const cents = item.price;
  const title = escapeHtml(item.product_title || item.title);
  const imageSrc = escapeHtml(item.image);
  const imageAlt = escapeHtml(item.title || item.product_title);
  const vatAttr = window.theme?.vat?.enabled ? ` data-vat-amount="${cents}"` : '';
  const vatLabel = window.theme?.vat?.enabled
    ? '<span data-vat-label class="vat-price-label text-navy"></span>'
    : '';
  const imageHtml = imageSrc
    ? `<img class="h-[90px] w-[100px] shrink-0 rounded-none object-cover" src="${imageSrc}" alt="${imageAlt}" width="100" height="90" loading="lazy" />`
    : '';

  return `<div class="flex items-center gap-4">
      ${imageHtml}
      <div class="flex min-w-0 flex-col gap-2.5">
        <p class="min-w-0 font-serif text-base-medium font-normal leading-[22px] text-black">${title}</p>
        <p class="inline-flex flex-wrap items-baseline gap-x-1.5 gap-y-0">
          <span class="text-black"${vatAttr}>${formatDisplayMoney(cents)}</span>${vatLabel}
        </p>
      </div>
    </div>`;
};

const renderAddedItemBlockHtml = (item, warrantyItem) => {
  let html = `<div class="border-b border-divider bg-white px-5 py-4 atc-notification-added-item">
      ${renderProductRowHtml(item)}`;

  if (warrantyItem) {
    html += renderWarrantyNestedHtml(warrantyItem);
  }

  html += '</div>';
  return html;
};

const populatePrimaryWarranty = (atcNotificationEl, warrantyItem) => {
  const warrantyEl = atcNotificationEl.querySelector('[data-atc-notification-warranty]');
  if (!warrantyEl) return;

  if (warrantyItem) {
    warrantyEl.innerHTML = renderWarrantyNestedHtml(warrantyItem);
    warrantyEl.classList.remove('hidden');
  } else {
    warrantyEl.innerHTML = '';
    warrantyEl.classList.add('hidden');
  }
};

const populatePrimaryItem = item => {
  const imageEl = document.querySelector('[data-atc-notification-image]');
  const titleEl = document.querySelector('[data-atc-notification-title]');
  const priceEl = document.querySelector('[data-atc-notification-price]');

  const title = item.product_title || item.title || '';
  const image = item.image || '';
  const cents = item.price;

  if (imageEl) {
    if (image) {
      imageEl.src = image;
      imageEl.alt = item.title || title;
      imageEl.classList.remove('hidden');
    } else {
      imageEl.classList.add('hidden');
    }
  }

  if (titleEl) {
    titleEl.textContent = title;
  }

  if (priceEl && typeof cents === 'number') {
    priceEl.textContent = formatDisplayMoney(cents);
    if (window.theme?.vat?.enabled) {
      priceEl.setAttribute('data-vat-amount', String(cents));
    }
  }
};

const loadRecommendations = async productId => {
  const relatedEl = document.querySelector('[data-atc-notification] [data-related-products]');
  if (!relatedEl || !productId) return;

  const baseUrl = window.Shopify?.routes?.root || '/';
  const url = `${baseUrl}recommendations/products?section_id=related-products&product_id=${productId}&limit=5`;
  relatedEl.dataset.url = url;
  relatedEl.classList.remove('hidden');
  relatedEl.innerHTML = '';

  try {
    const response = await fetch(url);
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const carouselEl = doc.querySelector('[data-related-products]');
    if (!carouselEl?.innerHTML.trim()) return;

    relatedEl.innerHTML = carouselEl.innerHTML;

    // Trustpilot errors inside dialogs — cards here aren't rendered with atc_short_label
    relatedEl
      .querySelectorAll('.product-card__reviews, [data-trustpilot-reviews-wrap], .trustpilot-widget')
      .forEach(el => el.remove());

    const drawerWrap = relatedEl.querySelector('.container');
    if (drawerWrap) {
      drawerWrap.className = 'border-t border-divider px-5 py-4';
      const heading = drawerWrap.querySelector('h2:not(.sr-only)');
      if (heading) {
        const label = heading.textContent.trim();
        const upsellHeading = document.createElement('p');
        upsellHeading.className = 'heading-24 mb-4 text-navy';
        upsellHeading.textContent = label;
        heading.replaceWith(upsellHeading);
      }
    }

    const sliderEl = relatedEl.querySelector('[data-swiper]');
    if (sliderEl) {
      const swiper = initSwiper(sliderEl);
      window.theme.swiperObjects = window.theme.swiperObjects || [];
      window.theme.swiperObjects.push(swiper);
    }
  } catch (err) {
    console.error('Error fetching ATC recommendations:', err);
  }
};

const closeAtcNotification = dialog => {
  if (!dialog?.open) return;
  dialog.classList.remove(VISIBLE_CLASS);
  window.setTimeout(() => {
    if (dialog.open) {
      dialog.close();
    }
  }, SETTINGS.FILTER_DRAW_ANIMATION_TIME);
};

const openAtcNotification = dialog => {
  if (!dialog || dialog.open) return;

  const handleBackdropClick = event => {
    const isInDialog = event.clientX >= dialog.getBoundingClientRect().left;
    if (!isInDialog) {
      closeAtcNotification(dialog);
    }
  };

  const handleCloseClick = event => {
    event.preventDefault();
    closeAtcNotification(dialog);
  };

  const handleCancel = event => {
    // Keep slide-out animation; don't let the UA dismiss instantly
    event.preventDefault();
    closeAtcNotification(dialog);
  };

  dialog.addEventListener('click', handleBackdropClick);
  dialog.addEventListener('cancel', handleCancel);
  dialog.querySelectorAll(CLOSE_SELECTOR).forEach(btn => {
    btn.addEventListener('click', handleCloseClick);
  });

  dialog.addEventListener(
    'close',
    () => {
      dialog.classList.remove(VISIBLE_CLASS);
      dialog.removeEventListener('click', handleBackdropClick);
      dialog.removeEventListener('cancel', handleCancel);
      dialog.querySelectorAll(CLOSE_SELECTOR).forEach(btn => {
        btn.removeEventListener('click', handleCloseClick);
      });
    },
    { once: true }
  );

  dialog.showModal();
  requestAnimationFrame(() => {
    dialog.classList.add(VISIBLE_CLASS);
  });
  trapFocus(dialog);
};

const toggleAtcNotification = cartAddResponse => {
  const atcNotificationEl = document.querySelector('[data-atc-notification]');
  if (!atcNotificationEl) return;

  // Keep dialog as a direct body child so showModal() top-layer works reliably
  if (atcNotificationEl.parentElement !== document.body) {
    document.body.appendChild(atcNotificationEl);
  }

  const items = getLineItems(cartAddResponse);
  const bodyEl = atcNotificationEl.querySelector('[data-atc-notification-body]');
  const { productItems, warrantyByParentId } = partitionAddedItems(items);

  if (bodyEl) {
    bodyEl.innerHTML = '';
  }

  populatePrimaryWarranty(atcNotificationEl, null);

  if (productItems.length > 0) {
    populatePrimaryItem(productItems[0]);
    populatePrimaryWarranty(
      atcNotificationEl,
      warrantyByParentId.get(String(productItems[0].product_id)) || null
    );

    if (bodyEl && productItems.length > 1) {
      bodyEl.innerHTML = productItems
        .slice(1)
        .map(item =>
          renderAddedItemBlockHtml(item, warrantyByParentId.get(String(item.product_id)) || null)
        )
        .join('');
    }

    if (productItems[0].product_id) {
      loadRecommendations(productItems[0].product_id);
    }
  }

  if (window.theme?.vat?.enabled) {
    notifyVatRefresh();
  }

  openAtcNotification(atcNotificationEl);
};

export default toggleAtcNotification;
